export type HtmlEditorMode = 'visual' | 'source';

export type VisualEditorAlign = 'left' | 'center' | 'right' | 'justify';
export type VisualEditorRowBorder = 'none' | 'light';
export const CELL_BORDER_EDGES = ['top', 'right', 'bottom', 'left'] as const;
export type VisualEditorCellEdge = (typeof CELL_BORDER_EDGES)[number];

export interface VisualEditorActiveLink {
    href: string;
    text: string;
    target: string | null;
}

export const EMAIL_REGIONS = ['canvas', 'header', 'card'] as const;
export type EmailRegion = (typeof EMAIL_REGIONS)[number];

export const HTML_EDITOR_HEADINGS = ['text', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6'] as const;
export type HtmlEditorHeading = (typeof HTML_EDITOR_HEADINGS)[number];

export interface VisualEditorSelectionStyle {
    color?: string;
    backgroundColor?: string;
    fontSize?: string;
    fontFamily?: string;
    bold?: boolean;
    italic?: boolean;
    underline?: boolean;
    heading?: HtmlEditorHeading;
    inTable?: boolean;
    cellBorderEdges?: VisualEditorCellEdge[];
    cellBorderColor?: string;
    cellFillColor?: string;
    activeLink?: VisualEditorActiveLink;
    align?: VisualEditorAlign;
    emailRegionColors?: Partial<Record<EmailRegion, string>>;
}

export type VisualEditorCommand =
    | { type: 'formatBlock'; block: 'p' | 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' }
    | { type: 'foreColor'; color: string }
    | { type: 'hiliteColor'; color: string }
    | { type: 'emailRegionFill'; region: EmailRegion; color: string }
    | { type: 'fontSize'; size: string }
    | { type: 'fontFamily'; family: string }
    | { type: 'bold' | 'italic' | 'underline' }
    | { type: 'insertUnorderedList' | 'insertOrderedList' | 'insertHorizontalRule' | 'unlink' }
    | { type: 'link'; href: string; text: string; target: string | null }
    | { type: 'image'; src: string; alt: string }
    | { type: 'insertTable'; rows: number; cols: number }
    | { type: 'addRow'; position: 'above' | 'below' }
    | { type: 'deleteRow' }
    | { type: 'addColumn'; position: 'before' | 'after'; columnIndex?: number }
    | { type: 'deleteColumn'; columnIndex?: number }
    | { type: 'deleteTable' }
    | { type: 'rowBorderBottom'; border: VisualEditorRowBorder }
    | { type: 'cellBorders'; edges: VisualEditorCellEdge[]; color: string }
    | { type: 'cellFill'; color: string }
    | { type: 'undo' | 'redo' }
    | { type: 'align'; align: VisualEditorAlign };
export const HTML_EDITOR_FONT_SIZES = ['8', '9', '10', '11', '12', '14', '18', '24', '30', '36', '48', '60', '72', '96'] as const;
export const HTML_EDITOR_ALIGNS = ['left', 'center', 'right', 'justify'] as const;
export const HTML_EDITOR_TABLE_DEFAULT_ROWS = 2;
export const HTML_EDITOR_TABLE_DEFAULT_COLS = 2;
export const HTML_EDITOR_TABLE_MIN_ROWS = 1;
export const HTML_EDITOR_TABLE_MAX_ROWS = 10;
export const HTML_EDITOR_TABLE_MIN_COLS = 1;
export const HTML_EDITOR_TABLE_MAX_COLS = 6;

export const stepHtmlEditorFontSize = (current: string, direction: 1 | -1): string => {
    const min = Number(HTML_EDITOR_FONT_SIZES[0]);
    const max = Number(HTML_EDITOR_FONT_SIZES[HTML_EDITOR_FONT_SIZES.length - 1]);
    const value = Number(current);
    const numeric = Number.isFinite(value) ? value : direction > 0 ? min : max;
    return String(Math.max(min, Math.min(max, numeric + direction)));
};

/** Preset sizes plus the current selection size when it is not already in the list. */
export const htmlEditorFontSizeOptions = (current?: string): string[] => {
    const presets = [...HTML_EDITOR_FONT_SIZES];
    if (!current || presets.includes(current as (typeof HTML_EDITOR_FONT_SIZES)[number])) return presets;
    if (!Number.isFinite(Number(current))) return presets;
    return [...presets, current].sort((a, b) => Number(a) - Number(b));
};

/** Email-safe stacks; labels are the primary family name shown in the toolbar. */
export const HTML_EDITOR_FONT_FAMILIES = [
    { value: 'Arial, Helvetica, sans-serif', label: 'Arial' },
    { value: 'Helvetica, Arial, sans-serif', label: 'Helvetica' },
    { value: 'Montserrat, Arial, Helvetica, sans-serif', label: 'Montserrat' },
    { value: 'Georgia, Times New Roman, serif', label: 'Georgia' },
    { value: '"Times New Roman", Times, serif', label: 'Times New Roman' },
    { value: 'Verdana, Geneva, sans-serif', label: 'Verdana' },
    { value: 'Tahoma, Geneva, sans-serif', label: 'Tahoma' },
    { value: '"Courier New", Courier, monospace', label: 'Courier New' }
] as const;

export const HTML_EDITOR_DEFAULT_FONT_FAMILY = HTML_EDITOR_FONT_FAMILIES[0].value;
